import {
	RideCheckbox as RideCheckboxBase,
	RideSelect as RideSelectBase,
	RideTextField as RideTextFieldBase,
} from '@rimac-seguros/ride-system-components';
import { useFormContext } from 'react-hook-form';

import type React from 'react';

import { handleInputAmount, handleInputNumber } from '../../utils';
import { RangeDatepicker } from '../RangeDatePicker';
import { CURRENT_PAGE_FIELD, ROWS_PER_PAGE } from './TableData.constants';
import { EInputType } from './TableData.types';

const RideTextField = RideTextFieldBase as React.ComponentType<any>;
const RideSelect = RideSelectBase as React.ComponentType<any>;
const RideCheckbox = RideCheckboxBase as React.ComponentType<any>;

export const useTableData = ({
	bodyTableData,
	tableName,
}: {
	bodyTableData: any[];
	tableName: string;
}) => {
	const methodsHookForm = useFormContext();
	const {
		control,
		register,
		setValue,
		formState: { errors },
		watch,
	} = methodsHookForm;
	const paginationName = `${tableName}_${CURRENT_PAGE_FIELD}`;

	const rowsActive = bodyTableData.filter((row) => !row.isDeletedRow);

	const handlePageChange = (page) => {
		setValue(`${paginationName}`, page);
	};

	const inputTypeToComponent = ({
		rowIndex,
		inputTypeToUse,
		validations,
		name,
		options = [],
		value,
		disabled = false,
		rowData,
		decimalNumber = 2,
		prefix,
		suffix,
	}) => {
		const baseInput = {
			defaultValue: value,
			disabled,
			error: !!errors?.[tableName]?.[rowIndex]?.[name],
			errorMessage: errors?.[tableName]?.[rowIndex]?.[name]?.message,
			...register(`${tableName}.${rowIndex}.${name}`, {
				...validations,
				onChange: (eventData) => {
					validations?.onChange?.({
						event: eventData,
						rowData,
						cellKey: name,
					});
				},
			}),
		};

		if (inputTypeToUse) {
			const inputType = {
				[EInputType.TEXT]: <RideTextField size="small" {...baseInput} />,
				[EInputType.AMOUNT]: (
					<RideTextField
						size="small"
						prefix={prefix}
						suffix={suffix}
						{...baseInput}
						onInput={(event) =>
							handleInputAmount(event, {
								allowNegative: false,
								decimalPlaces: decimalNumber,
							})
						}
					/>
				),
				[EInputType.NUMBER]: (
					<RideTextField
						size="small"
						prefix={prefix}
						suffix={suffix}
						{...baseInput}
						onInput={(event) =>
							handleInputNumber(event, {
								allowNegative: false,
								decimalPlaces: decimalNumber,
							})
						}
					/>
				),
				[EInputType.SELECT]: (
					<RideSelect
						size="small"
						options={options}
						value={value}
						disabled={disabled}
						className="tabledata-select"
						onChange={(event) => {
							const newValue = event?.target ? event.target.value : event;
							validations?.onChange?.({
								value: newValue,
								rowData,
								cellKey: name,
							});
						}}
					/>
				),
				[EInputType.DATE]: (
					<RangeDatepicker
						name={`${tableName}.${rowIndex}.${name}`}
						hookForm={methodsHookForm}
						//value={value}
						defaultValue={value ? new Date(value) : null}
						disabled={disabled}
						onChange={(event) => {
							validations?.onChange?.({
								event,
								rowData,
								cellKey: name,
							});
						}}
						placeholder="Selecciona fecha"
						showCalendarIcon={false}
					/>
				),
				[EInputType.CHECKBOX]: (
					<RideCheckbox
						name="radioSeleccion"
						checked={
							Array.isArray(validations?.radioSelected) &&
							validations.radioSelected
								.map(String)
								.includes(String(rowData.idRow))
						}
						onChange={() => validations?.onRadioChange?.(rowData?.idRow)}
						disabled={disabled}
					/>
				),
				[EInputType.BUTTON_ICON]: value,
			};

			return inputType[inputTypeToUse];
		}

		return <span />;
	};

	const handleGeneratePagesNumber = () => {
		const totalPages = Math.ceil(rowsActive.length / ROWS_PER_PAGE);
		const pages = [];
		for (let i = 1; i <= totalPages; i++) {
			pages.push(i);
		}
		return pages;
	};

	return {
		rowsActive,
		currentPage: watch(paginationName) ?? 1,
		rowsPerPage: ROWS_PER_PAGE,
		pages: handleGeneratePagesNumber(),
		currentRows: rowsActive,
		handlePageChange,
		inputTypeToComponent,
	};
};
